import { useSelector, useDispatch } from "react-redux";
import { arrayNameByLang, appStorageKey } from "../utils/global.js";
import { useLang } from "../hooks/";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setCurrentCompany } from "../store/currentCompanySlice.js";
import { setCurrentBranch } from "../store/currentBranchSlice.js";
import { setLang } from "../store/langSlice.js";

const useTenant = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation('global');

  const { langSelected } = useLang();

  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState({});
  const [companiesList, setCompaniesList] = useState([]);
  const [tenantId, setTenantId] = useState();
  const [tenantUsername, setTenantUsername] = useState();
  const [tenantImage, settenantImage] = useState();
  const [allAppSetting, setAllAppSetting] = useState();
  const [initialCompanyName, setInitialCompanyName] = useState();
  const [initialCompanyId, setInitialCompanyId] = useState();
  const [getSettingsByCompanySelected, setSettingsByCompanySelected] = useState(
    {}
  );

  const companySelected = useSelector((state) => state.currentCompany);
  const branchSelected = useSelector((state) => state.currentBranch);

  const safeParse = (value) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };
  const isSelectionKey = (key, suffix) => key && key.endsWith(suffix);
  const getStoredSelection = (keys) => {
    let selected = null;
    let selectedTime = -1;
    for (const key of keys) {
      const value = safeParse(localStorage.getItem(key));
      if (!value?.value) continue;
      const updatedAt = Number(value?.updatedAt || 0);
      if (updatedAt >= selectedTime) {
        selected = value;
        selectedTime = updatedAt;
      }
    }
    return selected;
  };
  const getSelectionKeys = (suffix, eventKey, primaryKey) => {
    const keys = new Set();
    if (primaryKey) keys.add(primaryKey);
    if (eventKey && isSelectionKey(eventKey, suffix)) keys.add(eventKey);
    try {
      Object.keys(localStorage).forEach((key) => {
        if (isSelectionKey(key, suffix)) keys.add(key);
      });
    } catch {
    }
    return Array.from(keys);
  };
  const getPreferredLangValue = () => {
    try {
      return localStorage.getItem("preferredLang") || localStorage.getItem("i18nextLng") || localStorage.getItem("appLang");
    } catch {
      return null;
    }
  };
  const normalizeLangValue = (value) =>
    String(value || "").toLowerCase().startsWith("ar") ? "ar" : "en";
  const applyLang = (value) => {
    if (!value) return;
    const normalized = normalizeLangValue(value);
    if (normalized !== langSelected?.value) {
      dispatch(setLang({ value: normalized }));
    }
    if (i18n.language !== normalized) {
      i18n.changeLanguage(normalized);
    }
  };
  const getCompanyById = (id) =>
    tenant?.companies?.filter((company) => company._id === id)[0];
  const getDefaultLangFromSettings = (settings) => {
    if (!settings) return null;
    if (Array.isArray(settings)) {
      const matched = settings.find(
        (item) =>
          item?.name === "DEFAULT_LANG" || item?.settingName === "DEFAULT_LANG"
      );
      if (matched?.value) return matched.value;
      return settings?.[5]?.value || null;
    }
    return settings?.DEFAULT_LANG || null;
  };
  const companyNameByLang = (company) =>
    company?.name?.filter(
      (companyname) => companyname.lang === langSelected.value
    )[0];
  const currentCompany = getCompanyById(companySelected?.value);
  const companyLabelByLang = (company, fallback) =>
    company?.name?.find?.((n) => n?.lang === langSelected.value)?.text ||
    company?.name?.find?.((n) => n?.lang === "ar")?.text ||
    company?.name?.find?.((n) => n?.lang === "en")?.text ||
    company?.name?.[0]?.text ||
    fallback;
  const branchLabelByLang = (branch, fallback) =>
    branch?.name?.find?.((n) => n?.lang === langSelected.value)?.text ||
    branch?.name?.find?.((n) => n?.lang === "ar")?.text ||
    branch?.name?.find?.((n) => n?.lang === "en")?.text ||
    branch?.name?.[0]?.text ||
    fallback;

  const tenantCompaniesByLang = (lang) => {
    return tenant?.companies?.map((company) => {
      return {
        companyId: company._id,
        companyName: arrayNameByLang(company.name, lang),
      };
    });
  };
  const companiesListHandler = () =>
    tenantCompaniesByLang(langSelected.value)?.map((item) => {
      return { label: item?.companyName?.text, value: item?.companyId };
    });

  // const [companiesList,setCompaniesList] = useState(companiesListHandller)

  const BranchesByCompanyId = (companyId) =>
    tenant?.companies?.filter((company) => company._id === companyId)[0]
      ?.branches;

  const branchesList = BranchesByCompanyId(companySelected.value)?.map(
    (branch) => {
      const branchId = branch._id;
      const branchName = branch.name.find(
        (name) => name.lang === langSelected.value
      ) || { text: "unknown" };
      return {
        label: branchName.text,
        value: branchId,
      };
    }
  );

  useEffect(() => {
    const tenantHandler = () => safeParse(localStorage.getItem("tenant"));
    if (tenantHandler()) {
      setTenant(tenantHandler());
    }
  }, []);
  useEffect(() => {
    if (Object.keys(tenant).length > 0) {
      setTenantId(tenant?._id);
      setTenantUsername(tenant?.username);
      settenantImage(tenant?.image);
      setAllAppSetting(tenant?.setting?.appSetting);
      setInitialCompanyName(tenant?.companies[0]?.name[0]?.text);
      setInitialCompanyId(tenant?.companies[0]?.value);
      if (localStorage.getItem("tenant")) {
        const langCompany = getCompanyById(companySelected?.value) || tenant?.companies?.[0];
        const defaultLang = getDefaultLangFromSettings(langCompany?.settings?.generalSettings);
        const appLang = langSelected?.value || defaultLang || "en";
        localStorage.setItem("appLang", appLang);
      }
      setSettingsByCompanySelected(currentCompany);
      const nextCompaniesList = companiesListHandler() || [];
      setCompaniesList(nextCompaniesList);
      const companySelectedValid = nextCompaniesList.some(
        (item) => item?.value === companySelected?.value
      );
      if (companySelectedValid) {
        const companyEntity = getCompanyById(companySelected?.value);
        const nextCompanyName = companyLabelByLang(
          companyEntity,
          companySelected?.name
        );
        if (nextCompanyName && nextCompanyName !== companySelected?.name) {
          dispatch(
            setCurrentCompany({
              name: nextCompanyName,
              value: companySelected?.value,
            })
          );
        }
      } else if (!companySelectedValid && nextCompaniesList[0]?.value) {
        dispatch(
          setCurrentCompany({
            name: nextCompaniesList[0]?.label,
            value: nextCompaniesList[0]?.value,
          })
        );
      }
      setLoading(false);
    }
  }, [tenant, companySelected?.value, langSelected.value]);

  useEffect(() => {
    if (!companySelected?.value) return;
    const companyBranches = BranchesByCompanyId(companySelected.value) || [];
    if (!companyBranches.length) return;
    const selectedBranchEntity = companyBranches.find(
      (branch) => branch?._id === branchSelected?.value
    );
    if (selectedBranchEntity) {
      const nextBranchName = branchLabelByLang(
        selectedBranchEntity,
        branchSelected?.name || (langSelected.value === "ar" ? "الفرع" : "Select a Branch")
      );
      if (nextBranchName && nextBranchName !== branchSelected?.name) {
        dispatch(
          setCurrentBranch({
            name: nextBranchName,
            value: branchSelected?.value,
          })
        );
      }
      return;
    }
    const nextBranch = companyBranches[0];
    const nextBranchName =
      arrayNameByLang(nextBranch?.name, langSelected.value)?.text ||
      arrayNameByLang(nextBranch?.name, "ar")?.text ||
      (langSelected.value === "ar" ? "الفرع" : "Select a Branch");
    dispatch(
      setCurrentBranch({ name: nextBranchName, value: nextBranch?._id })
    );
  }, [companySelected?.value, branchSelected?.value, tenant, langSelected.value]);

  useEffect(() => {
    if (!tenant || !tenant?.companies?.length) return;
    const syncFromStorage = (eventKey) => {
      const companyKeys = getSelectionKeys(':selectedCompany', eventKey, appStorageKey('selectedCompany'));
      const storedCompany = getStoredSelection(companyKeys);
      const storedCompanyId = storedCompany?.value;
      const storedCompanyEntity = storedCompanyId ? getCompanyById(storedCompanyId) : null;
      if (storedCompanyEntity && storedCompanyId !== companySelected?.value) {
        const nextCompanyName = companyLabelByLang(storedCompanyEntity, storedCompany?.name);
        dispatch(
          setCurrentCompany({
            name: nextCompanyName,
            value: storedCompanyId
          })
        );
      }
      const effectiveCompanyId = storedCompanyEntity?._id || companySelected?.value;
      const companyBranches = effectiveCompanyId ? BranchesByCompanyId(effectiveCompanyId) || [] : [];
      const branchKeys = getSelectionKeys(':selectedBranch', eventKey, appStorageKey('selectedBranch'));
      const storedBranch = getStoredSelection(branchKeys);
      const storedBranchId = storedBranch?.value;
      const storedBranchEntity = storedBranchId
        ? companyBranches.find((branch) => branch?._id === storedBranchId)
        : null;
      if (storedBranchEntity && storedBranchId !== branchSelected?.value) {
        const nextBranchName = branchLabelByLang(
          storedBranchEntity,
          storedBranch?.name || (langSelected.value === "ar" ? "الفرع" : "Select a Branch")
        );
        dispatch(
          setCurrentBranch({
            name: nextBranchName,
            value: storedBranchId
          })
        );
      }
    };
    syncFromStorage();
    const handleStorage = (event) => {
      if (isSelectionKey(event.key, ':selectedCompany') || isSelectionKey(event.key, ':selectedBranch')) {
        syncFromStorage(event.key);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [tenant, langSelected.value, companySelected?.value, branchSelected?.value]);

  useEffect(() => {
    const initialLang = getPreferredLangValue();
    applyLang(initialLang);
    const handleStorage = (event) => {
      if (event.key === "preferredLang" || event.key === "i18nextLng" || event.key === "appLang") {
        applyLang(event.newValue);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [dispatch, i18n, langSelected?.value]);

  return {
    loading,
    tenant,
    tenantId,
    tenantUsername,
    allAppSetting,
    initialCompanyName,
    initialCompanyId,
    companiesList,
    branchesList,
    tenantCompaniesByLang,
    BranchesByCompanyId,
    getCompanyById,
    companyNameByLang,
    getSettingsByCompanySelected,
    companySelected,
    branchSelected,
    langSelected,
  };
};

export default useTenant;
